import React, { useRef } from 'react';
import T from 'prop-types';
import { map, mapValues } from 'lodash-es';

import { SmartChart } from '../../../../../../components/Chart';
import { useZoomButtons } from '../../../../../../components/Chart/ChartControls/hooks';
import { colorsBySeries, namesBySeries, makeChartConfig } from './configs';

const emptyArray = [];

const zoomLimit = 9;
const serviceButtonsStyle = { bottom: 80 };
const orderedSeries = ['forecast', 'kpi'];

const getExtremesFromSeries = (seriesLength) => {
  if (seriesLength > zoomLimit) {
    return [seriesLength - zoomLimit, seriesLength - 1];
  }

  return emptyArray;
};

const QuarterlyPerformanceChart = ({
  view,
  series,
  positions,
  isScreenshot,
  onChartZoomChange,
}) => {
  const chartRef = useRef();

  const { categories = emptyArray, ...restSeries } = series;

  const [ZoomButtons, setShowServiceButtons] = useZoomButtons({
    chartRef,
    initialExtremes: isScreenshot
      ? [positions?.min, positions?.max]
      : getExtremesFromSeries(categories.length),
    resetRangeFlag: [categories.length],
    style: serviceButtonsStyle,
  });

  const chartSeries = orderedSeries.reduce((result, seriesKey) => {
    const seriesData = restSeries?.[seriesKey];

    if (!seriesData) return result;

    return {
      ...result,
      [seriesKey]: seriesData[view]?.map((y, x) => ({ y, x })),
    };
  }, {});

  const metaSeriesData = mapValues(chartSeries, (_, key) => ({
    name: namesBySeries[key],
    color: colorsBySeries[key],
    type: 'line',
  }));

  const handleChartZoomChange = (event) => {
    const { dataMax, min, max } = event;

    if (dataMax > zoomLimit) {
      setShowServiceButtons((Math.round(max) - Math.round(min)) !== (zoomLimit - 1));
    }

    if (onChartZoomChange) {
      const columnKey = categories[Math.round(max)];
      onChartZoomChange({ ...event, columnKey });
    }
  };

  const chartConfig = makeChartConfig({
    categories,
    view,
    zoomLimit,
    isScreenshot,
    onChartZoomChange: handleChartZoomChange,
  });

  return (
    <div>
      <div className="pos-rel">
        <SmartChart
          ref={chartRef}
          chartId="quarterly-performance-chart"
          type="stockChart"
          series={chartSeries}
          metaSeriesData={metaSeriesData}
          config={chartConfig}
        />
        {!isScreenshot && ZoomButtons}
      </div>
      <div className="quarterly-performance-chart__legend">
        {map(metaSeriesData, ({ name, color }, key) => (
          <div key={key} className="quarterly-performance-chart__legend-item">
            <span className="legend-item-marker" style={{ backgroundColor: color }} />
            <span>{name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

QuarterlyPerformanceChart.propTypes = {
  series: T.shape({
    categories: T.arrayOf(T.string),
    kpi: T.object,
    forecast: T.object,
  }).isRequired,
  view: T.string,
  positions: T.object,
  isScreenshot: T.bool,
  onChartZoomChange: T.func,
};

export default QuarterlyPerformanceChart;
